import { removeUndefined } from '@helpers/object';

export interface HelpHeroConfig {
  /** Helphero app ID */
  appId: string;
  /** ID for profile and settings tour */
  tourIdProfileAndSettings: string;
  /** ID for tax codes tour */
  tourIdTaxCodes: string;
  /** ID for filing screen tour */
  tourIdFilingScreen: string;
  /** ID for upload data tour */
  tourIdUploadingData: string;
}

export interface CompanyConfig {
  /** Company address */
  address: string;
  /** Company full name */
  registeredName: string;
  /** Company name */
  name: string;
  /** Country company is registered in */
  registeredCountry: string;
}

export interface TagManagerConfig {
  /** Container ID */
  gtmId: string;
  /** Used to store environment cookie */
  auth?: string;
  /** Used to set environment */
  preview?: string;
}

export interface AppConfig {
  /** API url use as base for all API calls */
  apiUrl: string;
  /** Owner company options */
  company?: CompanyConfig;
  /** Favicon */
  favicon?: string;
  /** Google tag manager config */
  googleTagManagerId: TagManagerConfig;
  /** Handsontable license key */
  handsOnTableKey: string;
  /** Help Hero config */
  helpHero: HelpHeroConfig;
  /** Sentry DSN */
  sentryDsn: string;
  /** App version */
  version: string;
  /** URL for external help site */
  helpUrl?: string;
  /** URL for external signup page */
  signUpUrl?: string;
  /** Email address for support queries */
  supportEmail?: string;
  /** Standard Contractual Clauses url */
  contractualClausesUrl?: string;
  /** Email address for legal queries */
  legalEmail?: string;
  /** Filing app config */
  vfile?: AppSectionConfig;
  /** Main app config */
  vsuite?: AppSectionConfig;
}

export interface AppSectionConfig {
  /** Logo */
  logo: string;
  /** Display name for the app section */
  name: string;
  /** Relative URL to app section */
  url: string;
}

export interface ExtendedAppConfig
  extends Omit<
    AppConfig,
    | 'apiUrl'
    | 'company'
    | 'favicon'
    | 'googleTagManagerId'
    | 'handsOnTableKey'
    | 'helpHero'
    | 'sentryDsn'
    | 'version'
  > {
  /** API url use as base for all API calls */
  apiUrl?: string;
  /** Owner company options */
  company: CompanyConfig;
  /** Favicon */
  favicon: string;
  /** Filing app config */
  vfile: AppSectionConfig;
  /** Main app config */
  vsuite: AppSectionConfig;
}

const {
  APP_VERSION,
  SENTRY_DSN,
  API_URL,
  TAG_MAN,
  TAG_MAN_AUTH,
  TAG_MAN_PREVIEW,
  HANDSONTABLE_KEY,
  HELPHERO_APP_ID,
  HELPHERO_PROFILE_AND_SETTINGS_TOUR_ID,
  HELPHERO_TAX_CODES_TOUR_ID,
  HELPHERO_FILING_SCREENS_TOUR_ID,
  HELPHERO_UPLOADING_DATA_TOUR_ID,
} = process.env;

export const baseConfig = {
  apiUrl: API_URL,
  googleTagManager: removeUndefined({
    gtmId: TAG_MAN,
    auth: TAG_MAN_AUTH,
    preview: TAG_MAN_PREVIEW,
  }),
  handsOnTableKey: HANDSONTABLE_KEY,
  helpHero: {
    appId: HELPHERO_APP_ID,
    tourIdProfileAndSettings: HELPHERO_PROFILE_AND_SETTINGS_TOUR_ID,
    tourIdTaxCodes: HELPHERO_TAX_CODES_TOUR_ID,
    tourIdFilingScreen: HELPHERO_FILING_SCREENS_TOUR_ID,
    tourIdUploadingData: HELPHERO_UPLOADING_DATA_TOUR_ID,
  },
  sentryDsn: SENTRY_DSN,
  version: APP_VERSION,
};
